import { useState, useEffect } from 'react';
import { UserProgress } from '../services/storage';
import { listeningSentences, ListeningSentence, listeningCategories } from '../data/listening-sentences';
import { ttsService } from '../services/tts';
import { storageService } from '../services/storage';
import { initMistakeReviewService, mistakeReviewService } from '../services/mistake-review';

interface SentenceListeningPageProps {
  user: UserProgress;
}

type PracticeMode = 'select' | 'translate' | 'fillBlank' | 'repeat';
type PlayMode = 'random' | 'sequential';

export default function SentenceListeningPage({ user }: SentenceListeningPageProps) {
  const [currentSentence, setCurrentSentence] = useState<ListeningSentence | null>(null);
  const [practiceMode, setPracticeMode] = useState<PracticeMode>('select');
  const [playMode, setPlayMode] = useState<PlayMode>('random');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [userAnswer, setUserAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [options, setOptions] = useState<string[]>([]);
  const [score, setScore] = useState(0);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard' | 'all'>('all');
  const [grade, setGrade] = useState<string>('all');
  const [category, setCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sentenceList, setSentenceList] = useState<ListeningSentence[]>([]);

  // Initialize mistake review service
  useEffect(() => {
    initMistakeReviewService(user.id);
  }, [user.id]);

  // Filter sentences based on all criteria
  useEffect(() => {
    let filtered = listeningSentences.filter(s => {
      if (difficulty !== 'all' && s.difficulty !== difficulty) return false;
      if (grade !== 'all' && s.grade !== grade) return false;
      if (category !== 'all' && s.category !== category) return false;

      // Search filter (中英文模糊搜索)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchSentence = s.sentence.toLowerCase().includes(query);
        const matchTranslation = s.translation.toLowerCase().includes(query);
        const matchKeywords = s.keywords.some(k => k.toLowerCase().includes(query));
        if (!matchSentence && !matchTranslation && !matchKeywords) return false;
      }

      return true;
    });

    // Shuffle if random mode
    if (playMode === 'random') {
      setSentenceList([...filtered].sort(() => Math.random() - 0.5));
    } else {
      setSentenceList(filtered);
    }
    setCurrentIndex(0);
  }, [difficulty, grade, category, searchQuery, playMode]);

  // Get current sentence
  useEffect(() => {
    if (sentenceList.length > 0 && currentIndex < sentenceList.length) {
      const sentence = sentenceList[currentIndex];
      setCurrentSentence(sentence);
      setShowAnswer(false);
      setUserAnswer('');
      setSelectedOption(null);

      // Generate options for translate mode
      if (practiceMode === 'translate') {
        generateTranslationOptions(sentence);
      }
    }
  }, [currentIndex, sentenceList, practiceMode]);

  // Get next sentence
  const getNextSentence = () => {
    if (currentIndex < sentenceList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Reached the end
      alert(`🎉 练习完成！\n正确率: ${totalAttempts > 0 ? Math.round((score / totalAttempts) * 100) : 0}%\n获得积分: ${score * 10}`);
      // Reset to beginning
      setCurrentIndex(0);
      setScore(0);
      setTotalAttempts(0);
    }
  };

  // Get previous sentence
  const getPreviousSentence = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Generate translation options
  const generateTranslationOptions = (sentence: ListeningSentence) => {
    const correctAnswer = sentence.translation;
    const otherSentences = listeningSentences.filter(s => s.id !== sentence.id);
    const randomSentences = otherSentences.sort(() => Math.random() - 0.5).slice(0, 3);
    const wrongAnswers = randomSentences.map(s => s.translation);
    const allOptions = [correctAnswer, ...wrongAnswers].sort(() => Math.random() - 0.5);
    setOptions(allOptions);
  };

  // Play sentence
  const handlePlay = async () => {
    if (!currentSentence || isPlaying) return;

    setIsPlaying(true);
    try {
      await ttsService.speakNormal(currentSentence.sentence);
    } catch (error) {
      console.error('TTS error:', error);
    }
    setIsPlaying(false);
  };

  // Check answer for translate mode
  const handleCheckTranslation = (selected: string) => {
    setSelectedOption(selected);
    setShowAnswer(true);
    setTotalAttempts(prev => prev + 1);

    if (selected === currentSentence?.translation) {
      setScore(prev => prev + 1);
      storageService.addPoints(user.id, 5);
    } else {
      // Record mistake when wrong
      if (mistakeReviewService && currentSentence) {
        mistakeReviewService.addMistake(
          'listening',
          currentSentence.id,
          currentSentence.sentence,
          selected,
          currentSentence.translation
        );
      }
    }
  };

  // Check answer for fill-blank mode
  const handleCheckFillBlank = () => {
    if (!currentSentence || !userAnswer.trim()) return;

    setShowAnswer(true);
    setTotalAttempts(prev => prev + 1);

    const keywords = currentSentence.keywords.map(k => k.toLowerCase());
    const userAnswerLower = userAnswer.toLowerCase().trim();

    if (keywords.some(keyword => userAnswerLower.includes(keyword))) {
      setScore(prev => prev + 1);
      storageService.addPoints(user.id, 10);
    } else {
      // Record mistake when wrong
      if (mistakeReviewService) {
        mistakeReviewService.addMistake(
          'listening',
          currentSentence.id,
          currentSentence.sentence,
          userAnswer.trim(),
          keywords.join(', ')
        );
      }
    }
  };

  // Render practice mode selector
  const renderModeSelector = () => (
    <div className="bg-white rounded-3xl p-6 shadow-soft">
      <h3 className="font-bold text-gray-800 mb-4">选择练习模式</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button
          onClick={() => { setPracticeMode('translate'); getNextSentence(); }}
          className={`p-4 rounded-2xl font-bold transition-all ${
            practiceMode === 'translate'
              ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🎯 听句子选翻译
        </button>
        <button
          onClick={() => { setPracticeMode('fillBlank'); getNextSentence(); }}
          className={`p-4 rounded-2xl font-bold transition-all ${
            practiceMode === 'fillBlank'
              ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          ✏️ 听句子填空
        </button>
        <button
          onClick={() => { setPracticeMode('repeat'); getNextSentence(); }}
          className={`p-4 rounded-2xl font-bold transition-all ${
            practiceMode === 'repeat'
              ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🗣️ 听句子跟读
        </button>
      </div>
    </div>
  );

  // Render filters
  const renderFilters = () => (
    <div className="bg-white rounded-3xl p-6 shadow-soft">
      {/* Quick Category Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          🎯 快速筛选分类 (点击选择)
        </label>
        <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-2">
          <button
            onClick={() => setCategory('all')}
            className={`p-3 rounded-xl text-center transition-all ${
              category === 'all'
                ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg'
                : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
            }`}
          >
            <div className="text-2xl mb-1">📚</div>
            <div className="text-xs font-medium">全部</div>
            <div className="text-xs opacity-75">{listeningSentences.length}句</div>
          </button>
          {listeningCategories.slice(0, 14).map(cat => {
            const count = listeningSentences.filter(s => s.category === cat.id).length;
            if (count === 0) return null;
            return (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className={`p-3 rounded-xl text-center transition-all ${
                  category === cat.id
                    ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}
              >
                <div className="text-2xl mb-1">{cat.icon}</div>
                <div className="text-xs font-medium">{cat.name}</div>
                <div className="text-xs opacity-75">{count}句</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search Box */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          🔍 辅助搜索 (在筛选结果中进一步查找)
        </label>
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="输入关键词，例如: hello、苹果、天气..."
            className="w-full px-4 py-3 pr-10 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-500">
            在当前筛选结果中找到 <span className="font-bold text-indigo-600">{sentenceList.length}</span> 个匹配句子
          </p>
        )}
      </div>

      {/* Advanced Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">播放模式</label>
          <select
            value={playMode}
            onChange={(e) => setPlayMode(e.target.value as PlayMode)}
            className="w-full p-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="random">随机</option>
            <option value="sequential">顺序</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">难度</label>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value as any)}
            className="w-full p-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">全部难度</option>
            <option value="easy">简单</option>
            <option value="medium">中等</option>
            <option value="hard">困难</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">年级</label>
          <select
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className="w-full p-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">全部年级</option>
            <option value="3">三年级</option>
            <option value="4">四年级</option>
            <option value="5">五年级</option>
            <option value="6">六年级</option>
            <option value="7">七年级</option>
            <option value="8">八年级</option>
            <option value="9">九年级</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">更多分类</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full p-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">全部分类</option>
            {listeningCategories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-4 text-center text-sm text-gray-500">
        共找到 <span className="font-bold text-indigo-600">{sentenceList.length}</span> 个句子
      </div>
    </div>
  );

  // Render score
  const renderScore = () => (
    <div className="bg-gradient-to-r from-green-400 to-blue-500 rounded-3xl p-6 text-white shadow-xl">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm opacity-80">正确率</p>
          <p className="text-3xl font-bold">
            {totalAttempts > 0 ? Math.round((score / totalAttempts) * 100) : 0}%
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm opacity-80">得分</p>
          <p className="text-3xl font-bold">{score * 10}</p>
        </div>
      </div>
    </div>
  );

  // Render translate mode
  const renderTranslateMode = () => (
    <div className="space-y-6">
      {/* Play button */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl p-8 text-white shadow-xl">
        <div className="text-center mb-6">
          <p className="text-sm opacity-80 mb-2">听句子，选择正确的翻译</p>
          <button
            onClick={handlePlay}
            disabled={isPlaying}
            className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto hover:bg-white/30 transition-all"
          >
            {isPlaying ? (
              <span className="text-4xl">🔊</span>
            ) : (
              <span className="text-4xl">▶️</span>
            )}
          </button>
          <p className="text-sm opacity-60 mt-2">点击播放</p>
        </div>
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 gap-3">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => !showAnswer && handleCheckTranslation(option)}
            disabled={showAnswer}
            className={`p-5 rounded-2xl font-medium text-left transition-all ${
              showAnswer
                ? option === currentSentence?.translation
                  ? 'bg-green-100 border-2 border-green-400 text-green-800'
                  : option === selectedOption
                  ? 'bg-red-100 border-2 border-red-400 text-red-800'
                  : 'bg-gray-50 text-gray-500'
                : 'bg-white hover:bg-indigo-50 border-2 border-gray-200 hover:border-indigo-300'
            }`}
          >
            <span className="mr-3 font-bold">{['A', 'B', 'C', 'D'][index]}.</span>
            {option}
          </button>
        ))}
      </div>

      {/* Navigation buttons */}
      {showAnswer && (
        <div className="flex gap-4">
          <button
            onClick={getPreviousSentence}
            disabled={currentIndex === 0}
            className="flex-1 py-4 bg-gray-100 text-gray-700 rounded-2xl font-bold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            ← 上一题
          </button>
          <button
            onClick={getNextSentence}
            className="flex-1 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl font-bold hover:opacity-90 transition-opacity"
          >
            {currentIndex < sentenceList.length - 1 ? '下一题 →' : '完成练习 🎉'}
          </button>
        </div>
      )}
    </div>
  );

  // Render fill-blank mode
  const renderFillBlankMode = () => {
    if (!currentSentence) return null;

    const words = currentSentence.sentence.split(' ');
    const blankIndices = currentSentence.blanks || [];

    return (
      <div className="space-y-6">
        {/* Play button */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl p-8 text-white shadow-xl">
          <div className="text-center mb-6">
            <p className="text-sm opacity-80 mb-2">听句子，填写缺失的单词</p>
            <button
              onClick={handlePlay}
              disabled={isPlaying}
              className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto hover:bg-white/30 transition-all"
            >
              {isPlaying ? (
                <span className="text-4xl">🔊</span>
              ) : (
                <span className="text-4xl">▶️</span>
              )}
            </button>
          </div>

          {/* Sentence with blanks */}
          <div className="text-center text-2xl font-bold">
            {words.map((word, index) => (
              <span key={index} className="inline-block mx-1">
                {blankIndices.includes(index) ? (
                  <span className="inline-block border-b-4 border-white min-w-[100px] h-8">
                    {showAnswer ? word : ''}
                  </span>
                ) : (
                  word
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="bg-white rounded-3xl p-6 shadow-soft">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            输入你听到的单词（多个单词用逗号分隔）
          </label>
          <input
            type="text"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            disabled={showAnswer}
            placeholder="例如: book, pen"
            className="w-full p-4 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-lg"
          />
        </div>

        {/* Check button */}
        {!showAnswer && (
          <button
            onClick={handleCheckFillBlank}
            disabled={!userAnswer.trim()}
            className="w-full py-4 bg-blue-500 text-white rounded-2xl font-bold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            检查答案
          </button>
        )}

        {/* Answer */}
        {showAnswer && (
          <>
            <div className="bg-green-100 rounded-2xl p-6">
              <p className="font-bold text-green-800 mb-2">正确答案：</p>
              <p className="text-xl font-bold text-green-700">{currentSentence.keywords.join(', ')}</p>
              <p className="text-gray-700 mt-2">{currentSentence.translation}</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={getPreviousSentence}
                disabled={currentIndex === 0}
                className="flex-1 py-4 bg-gray-100 text-gray-700 rounded-2xl font-bold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                ← 上一题
              </button>
              <button
                onClick={getNextSentence}
                className="flex-1 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl font-bold hover:opacity-90 transition-opacity"
              >
                {currentIndex < sentenceList.length - 1 ? '下一题 →' : '完成练习 🎉'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // Render repeat mode
  const renderRepeatMode = () => {
    if (!currentSentence) return null;

    return (
      <div className="space-y-6">
        {/* Play button */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl p-8 text-white shadow-xl">
          <div className="text-center mb-6">
            <p className="text-sm opacity-80 mb-2">听句子并跟读</p>
            <button
              onClick={handlePlay}
              disabled={isPlaying}
              className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto hover:bg-white/30 transition-all"
            >
              {isPlaying ? (
                <span className="text-4xl">🔊</span>
              ) : (
                <span className="text-4xl">▶️</span>
              )}
            </button>
          </div>

          {showAnswer && (
            <div className="text-center">
              <p className="text-2xl font-bold mb-2">{currentSentence.sentence}</p>
              <p className="text-lg opacity-80">{currentSentence.translation}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={handlePlay}
            disabled={isPlaying}
            className="flex-1 py-4 bg-blue-100 text-blue-700 rounded-2xl font-bold hover:bg-blue-200 transition-colors"
          >
            🔊 再听一次
          </button>
          <button
            onClick={() => setShowAnswer(!showAnswer)}
            className="flex-1 py-4 bg-purple-100 text-purple-700 rounded-2xl font-bold hover:bg-purple-200 transition-colors"
          >
            {showAnswer ? '🙈 隐藏答案' : '👀 显示答案'}
          </button>
        </div>

        {/* Navigation buttons */}
        <div className="flex gap-4">
          <button
            onClick={getPreviousSentence}
            disabled={currentIndex === 0}
            className="flex-1 py-4 bg-gray-100 text-gray-700 rounded-2xl font-bold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            ← 上一句
          </button>
          <button
            onClick={getNextSentence}
            className="flex-1 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl font-bold hover:opacity-90 transition-opacity"
          >
            {currentIndex < sentenceList.length - 1 ? '下一句 →' : '完成练习 🎉'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">🎧 句子听力练习</h1>
        <p className="text-gray-500">听句子，提高听力理解能力</p>
      </div>

      {/* Score and Progress */}
      <div className="grid grid-cols-2 gap-4">
        {renderScore()}
        <div className="bg-gradient-to-r from-purple-400 to-pink-500 rounded-3xl p-6 text-white shadow-xl">
          <div className="text-center">
            <p className="text-sm opacity-80">进度</p>
            <p className="text-3xl font-bold">
              {currentIndex + 1} / {sentenceList.length}
            </p>
          </div>
        </div>
      </div>

      {/* Mode selector */}
      {renderModeSelector()}

      {/* Filters */}
      {renderFilters()}

      {/* Practice area */}
      {currentSentence && (
        <div className="bg-white rounded-3xl p-6 shadow-soft">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">
              难度: <span className="font-bold">{currentSentence.difficulty}</span>
            </span>
            <span className="text-sm text-gray-500">
              年级: <span className="font-bold">{currentSentence.grade}年级</span>
            </span>
          </div>

          {practiceMode === 'translate' && renderTranslateMode()}
          {practiceMode === 'fillBlank' && renderFillBlankMode()}
          {practiceMode === 'repeat' && renderRepeatMode()}
        </div>
      )}

      {/* Tips */}
      <div className="bg-yellow-50 rounded-3xl p-6 border-2 border-yellow-200">
        <h3 className="font-bold text-yellow-800 mb-2">💡 练习提示</h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• 可以多次点击播放按钮反复听</li>
          <li>• 建议先从简单难度开始，逐步提高</li>
          <li>• 跟读练习可以帮助提高发音和语调</li>
          <li>• 填空练习可以加深对关键词的印象</li>
        </ul>
      </div>
    </div>
  );
}
